// Reads the tables in an .xlsx workbook, in the browser, with no library (AC4). An .xlsx is a zip of
// XML parts; this reads only what the Timecard Tool needs: Excel tables (and, if a sheet has none,
// the sheet itself), shared strings, and plain cell values. The file never leaves the browser; only
// the rows the import keeps are sent to the API (SEC11).

import type { Cell } from "./actuals";

export interface SheetTable {
  /** The Excel table's name ("DataChris"), or the sheet's name when the sheet has no table. */
  name: string;
  sheet: string;
  headers: string[];
  rows: Cell[][];
}

// --- Zip -------------------------------------------------------------------------------------------

async function inflate(data: Uint8Array): Promise<Uint8Array> {
  // slice() copies into a plain ArrayBuffer, which Blob requires.
  const stream = new Blob([data.slice()]).stream().pipeThrough(new DecompressionStream("deflate-raw"));
  return new Uint8Array(await new Response(stream).arrayBuffer());
}

/** The files in a zip, by path, decompressed on demand. */
export function readZip(buffer: ArrayBuffer): Map<string, () => Promise<Uint8Array>> {
  const bytes = new Uint8Array(buffer);
  const view = new DataView(buffer);
  let eocd = -1;
  for (let i = bytes.length - 22; i >= Math.max(0, bytes.length - 65_557); i--) {
    if (view.getUint32(i, true) === 0x06054b50) {
      eocd = i;
      break;
    }
  }
  if (eocd < 0) throw new Error("This isn't an .xlsx file (no zip directory found).");
  const count = view.getUint16(eocd + 10, true);
  let at = view.getUint32(eocd + 16, true);
  const files = new Map<string, () => Promise<Uint8Array>>();
  const decoder = new TextDecoder();
  for (let n = 0; n < count; n++) {
    if (view.getUint32(at, true) !== 0x02014b50) throw new Error("The workbook's zip directory is damaged.");
    const method = view.getUint16(at + 10, true);
    const size = view.getUint32(at + 20, true);
    const nameLength = view.getUint16(at + 28, true);
    const extraLength = view.getUint16(at + 30, true);
    const commentLength = view.getUint16(at + 32, true);
    const local = view.getUint32(at + 42, true);
    const name = decoder.decode(bytes.subarray(at + 46, at + 46 + nameLength));
    files.set(name, async () => {
      const start = local + 30 + view.getUint16(local + 26, true) + view.getUint16(local + 28, true);
      const data = bytes.subarray(start, start + size);
      if (method === 0) return data;
      if (method === 8) return inflate(data);
      throw new Error(`The workbook uses a zip method this reader doesn't support (${method}).`);
    });
    at += 46 + nameLength + extraLength + commentLength;
  }
  return files;
}

// --- XML (just enough for workbook parts) ----------------------------------------------------------

const ENTITIES: Record<string, string> = { amp: "&", lt: "<", gt: ">", quot: '"', apos: "'" };
export const unescapeXml = (s: string) =>
  s.replace(/&(#x[0-9a-f]+|#\d+|amp|lt|gt|quot|apos);/gi, (_, e: string) =>
    e[0] === "#" ? String.fromCodePoint(e[1].toLowerCase() === "x" ? parseInt(e.slice(2), 16) : Number(e.slice(1))) : ENTITIES[e.toLowerCase()],
  );

const attr = (tag: string, name: string) => {
  const m = tag.match(new RegExp(`\\s${name}="([^"]*)"`));
  return m ? unescapeXml(m[1]) : null;
};

/** The text of every <t> inside an element (rich text has several runs). */
const textOf = (xml: string) => [...xml.matchAll(/<t(?:\s[^>]*)?>([\s\S]*?)<\/t>|<t(?:\s[^>]*)?\/>/g)].map((m) => unescapeXml(m[1] ?? "")).join("");

/** "C12" to [row 12, column 2] (zero-based column). */
export function cellRef(ref: string): [number, number] {
  const m = ref.match(/^\$?([A-Z]+)\$?(\d+)$/i);
  if (!m) throw new Error(`Not a cell reference: ${ref}`);
  const col = [...m[1].toUpperCase()].reduce((n, ch) => n * 26 + ch.charCodeAt(0) - 64, 0) - 1;
  return [Number(m[2]), col];
}

/** A resolved relationship target ("../tables/table1.xml" from "xl/worksheets/") to a zip path. */
function resolvePath(base: string, target: string) {
  if (target.startsWith("/")) return target.slice(1);
  const parts = base.split("/").slice(0, -1);
  for (const p of target.split("/")) {
    if (p === "..") parts.pop();
    else if (p !== ".") parts.push(p);
  }
  return parts.join("/");
}

async function readText(files: Map<string, () => Promise<Uint8Array>>, path: string): Promise<string | null> {
  const f = files.get(path);
  return f ? new TextDecoder().decode(await f()) : null;
}

async function relationships(files: Map<string, () => Promise<Uint8Array>>, part: string): Promise<Map<string, string>> {
  const dir = part.split("/").slice(0, -1).join("/");
  const file = part.split("/").at(-1);
  const xml = (await readText(files, `${dir}/_rels/${file}.rels`)) ?? "";
  const out = new Map<string, string>();
  for (const m of xml.matchAll(/<Relationship\b[^>]*>/g)) {
    const id = attr(m[0], "Id");
    const target = attr(m[0], "Target");
    if (id && target) out.set(id, resolvePath(part, target));
  }
  return out;
}

/** A sheet's cells, by row number then column. */
function readCells(xml: string, shared: readonly string[]): Map<number, Map<number, Cell>> {
  const rows = new Map<number, Map<number, Cell>>();
  for (const m of xml.matchAll(/<c\b([^>]*?)(?:\/>|>([\s\S]*?)<\/c>)/g)) {
    const tag = m[1];
    const ref = attr(tag, "r");
    if (!ref) continue;
    const [r, c] = cellRef(ref);
    const type = attr(tag, "t") ?? "n";
    const inner = m[2] ?? "";
    const v = inner.match(/<v>([\s\S]*?)<\/v>/)?.[1];
    let value: Cell = null;
    if (type === "s" && v !== undefined) value = shared[Number(v)] ?? "";
    else if (type === "inlineStr") value = textOf(inner);
    else if (type === "str" && v !== undefined) value = unescapeXml(v);
    else if (type === "b" && v !== undefined) value = v === "1";
    else if (type === "e") value = null;
    else if (v !== undefined) value = Number(v);
    if (!rows.has(r)) rows.set(r, new Map());
    rows.get(r)!.set(c, value);
  }
  return rows;
}

function block(cells: Map<number, Map<number, Cell>>, firstRow: number, lastRow: number, firstCol: number, lastCol: number): Cell[][] {
  const out: Cell[][] = [];
  for (let r = firstRow; r <= lastRow; r++) {
    const row: Cell[] = [];
    for (let c = firstCol; c <= lastCol; c++) row.push(cells.get(r)?.get(c) ?? null);
    out.push(row);
  }
  return out;
}

/** Every Excel table in the workbook; a sheet with no table is read whole, its first row as headers. */
export async function readWorkbookTables(buffer: ArrayBuffer): Promise<SheetTable[]> {
  const files = readZip(buffer);
  const workbook = await readText(files, "xl/workbook.xml");
  if (!workbook) throw new Error("This isn't an Excel workbook (no xl/workbook.xml).");
  const sharedXml = (await readText(files, "xl/sharedStrings.xml")) ?? "";
  const shared = [...sharedXml.matchAll(/<si>([\s\S]*?)<\/si>|<si\/>/g)].map((m) => textOf(m[1] ?? ""));
  const workbookRels = await relationships(files, "xl/workbook.xml");
  const out: SheetTable[] = [];
  for (const m of workbook.matchAll(/<sheet\b[^>]*>/g)) {
    const sheetName = attr(m[0], "name") ?? "Sheet";
    const relId = attr(m[0], "r:id");
    const path = relId ? workbookRels.get(relId) : undefined;
    const xml = path ? await readText(files, path) : null;
    if (!path || !xml) continue;
    const cells = readCells(xml, shared);
    const tableRels = [...(await relationships(files, path)).values()].filter((p) => /\/tables\/[^/]+\.xml$/.test(p));
    let found = 0;
    for (const tablePath of tableRels) {
      const t = await readText(files, tablePath);
      const tag = t?.match(/<table\b[^>]*>/)?.[0];
      const ref = tag && attr(tag, "ref");
      if (!tag || !ref) continue;
      const [from, to] = ref.split(":");
      const [r1, c1] = cellRef(from);
      const [r2, c2] = cellRef(to ?? from);
      const headerRows = Number(attr(tag, "headerRowCount") ?? "1");
      const grid = block(cells, r1, r2, c1, c2);
      const columnNames = [...(t!.matchAll(/<tableColumn\b[^>]*>/g))].map((c) => attr(c[0], "name") ?? "");
      const headers = headerRows > 0 ? grid[0].map((h, i) => String(h ?? columnNames[i] ?? "").trim()) : columnNames;
      // The totals row, if shown, is not data.
      const totals = Number(attr(tag, "totalsRowCount") ?? "0");
      out.push({ name: attr(tag, "displayName") ?? attr(tag, "name") ?? sheetName, sheet: sheetName, headers, rows: grid.slice(headerRows > 0 ? 1 : 0, totals ? -totals : undefined) });
      found++;
    }
    if (!found && cells.size) {
      const rowNumbers = [...cells.keys()].sort((a, b) => a - b);
      const lastCol = Math.max(...[...cells.values()].flatMap((r) => [...r.keys()]));
      const grid = block(cells, rowNumbers[0], rowNumbers.at(-1)!, 0, lastCol);
      out.push({ name: sheetName, sheet: sheetName, headers: grid[0].map((h) => String(h ?? "").trim()), rows: grid.slice(1) });
    }
  }
  return out;
}
